import { DisplayOnBreakpoints } from "@dotcom/ui";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import {
  Colors,
  Divider,
  MediaQueries,
  Opacities,
  Paragraph,
  Spacer,
  Spacings,
  withOpacity,
} from "@polestar/component-warehouse-react";
import { useNavigate } from "@remix-run/react";
import { type FC, type MutableRefObject } from "react";

import useIntersectingSections from "../../hooks/useIntersectingSections";

import { LinkButton } from "~/common/components/LinkButton";

interface ArticleLink {
  title: string;
  slug: string;
}
interface Props {
  readonly mobileTitle: string;
  readonly links: ArticleLink[];
  readonly sectionRefs: MutableRefObject<HTMLElement[]>;
}

const StyledStickyWrapper = styled.div`
  position: sticky;
  top: ${Spacings.xxxxxLarge};
  margin-bottom: ${Spacings.xxxLarge};
  ${MediaQueries.tablet} {
    margin-bottom: ${Spacings.xxxxLarge};
  }
`;

const activeColor = withOpacity(Colors.black, Opacities.medium);

const StyledLinkButton = styled(LinkButton)<{ isActive: boolean }>(({
  isActive,
}) => {
  return css`
    display: flex;
    color: ${activeColor};
    text-decoration: none;
    text-align: left;

    &:hover {
      color: ${activeColor};
      text-decoration: none;
    }
    &:focus {
      ${isActive ? `color: ${activeColor}` : `color: ${Colors.black}`};
    }
    ${MediaQueries.tablet} {
      color: ${isActive ? activeColor : Colors.black};
    }
  `;
});

const StyledNumber = styled.span`
  min-width: ${Spacings.large};
`;

const StyledDivider = styled(Divider)`
  background: ${withOpacity(Colors.black, Opacities.xLow)};
`;

const intersectionObserverOptions = {
  threshold: 0,
  rootMargin: `0px 0px -60% 0px`,
};

const InPageNavigation: FC<Props> = ({ mobileTitle, links, sectionRefs }) => {
  const navigate = useNavigate();
  const activeSectionIndex = useIntersectingSections(
    sectionRefs.current,
    intersectionObserverOptions
  );

  const handleClick = (slug: string) => {
    navigate(`#${slug}`, {
      preventScrollReset: true,
    });
  };

  return (
    <StyledStickyWrapper>
      <DisplayOnBreakpoints display={{ mobile: "block", tablet: "none" }}>
        <StyledDivider spacing={Spacings.xxLarge} />
        <Paragraph>{mobileTitle}</Paragraph>
        <Spacer />
      </DisplayOnBreakpoints>

      <ul>
        {links.map(({ title, slug }, index) => {
          const isActive = index === activeSectionIndex;
          const isLast = index === links.length - 1;
          const [number, text] = title.split(/(\d+\.)/).slice(1);
          return (
            <li key={slug}>
              <StyledLinkButton
                isActive={isActive}
                onClick={() => {
                  handleClick(slug);
                }}
              >
                <StyledNumber>{number}</StyledNumber>
                {text}
              </StyledLinkButton>
              {!isLast && <Spacer spacing={Spacings.xSmall} />}
            </li>
          );
        })}
      </ul>

      <DisplayOnBreakpoints display={{ mobile: "block", tablet: "none" }}>
        <StyledDivider />
      </DisplayOnBreakpoints>
    </StyledStickyWrapper>
  );
};

export default InPageNavigation;
